import DOMPurify from "dompurify";

// Restricts hrefs to same-origin paths (no scheme, no protocol-relative).
// Matches the rule applied historically in Flash and Notice, with one
// hardening: the exclusion class also rejects backslash. Per WHATWG URL,
// browsers parsing a special-scheme URL treat `/\` as an authority delimiter
// identical to `//`, so `href="/\evil.com"` resolves to `http://evil.com`.
// DOMPurify checks ALLOWED_URI_REGEXP against the raw attribute string, not
// the resolved URL, so the exclusion has to do the work.
const RELATIVE_URI = /^\/[^/\\]/;

// DOMPurify hard-codes a data: URI allowance on `img`, `audio`, `video`,
// `source`, `image`, and `track` regardless of ALLOWED_URI_REGEXP - intended
// to support inline base64 images. We don't want that exception: a
// `data:image/svg+xml,<svg onload=alert(1)>` URI on an <img> still executes
// in some contexts, and we'd rather force authors to host their images. The
// hook below catches dangerous schemes on every URI-bearing attribute and
// drops the attribute, regardless of which built-in safe-list DOMPurify
// would normally apply.
const DANGEROUS_URI_SCHEMES = /^\s*(data|javascript|vbscript|file|blob):/i;
const URI_BEARING_ATTRS = new Set([
  "src",
  "href",
  "xlink:href",
  "action",
  "formaction",
  "background",
  "poster",
  "srcset",
]);

DOMPurify.addHook("uponSanitizeAttribute", (_node, data) => {
  if (
    URI_BEARING_ATTRS.has(data.attrName) &&
    DANGEROUS_URI_SCHEMES.test(data.attrValue)
  ) {
    data.keepAttr = false;
  }
});

/**
 * sanitizeInlineMarkup — tightest allowlist, intended for short pieces of
 * trusted-but-defence-in-depthed text (flash messages, banner bodies that
 * already went through Rails sanitisation). Only inline links to same-origin
 * paths survive.
 *
 * Use this for surfaces where the producer (Rails helper, backend flash)
 * already sanitised the input and React-side sanitisation is the second
 * layer of defence.
 */
export const sanitizeInlineMarkup = (input: string | null | undefined) =>
  DOMPurify.sanitize(input ?? "", {
    ALLOWED_TAGS: ["a"],
    ALLOWED_ATTR: ["href", "data-method"],
    ALLOWED_URI_REGEXP: RELATIVE_URI,
  });

/**
 * sanitizeRichText — mirror of DashboardNoticeHelper::SANITISE_TAGS on the
 * Rails side. Use for content that an admin types as light HTML (banner
 * body text, dashboard notices). Keeps href so links work; same-origin URI
 * restriction prevents the URI from carrying a script payload.
 *
 * Tag set deliberately matches the server-side allowlist so the trust
 * boundary is identical regardless of which side did the rendering.
 */
export const sanitizeRichText = (input: string | null | undefined) =>
  DOMPurify.sanitize(input ?? "", {
    ALLOWED_TAGS: ["a", "b", "br", "em", "i", "p", "strong"],
    ALLOWED_ATTR: ["href"],
    ALLOWED_URI_REGEXP: RELATIVE_URI,
  });

/**
 * sanitizeMarketingHtml — wider allowlist for Contentful-sourced marketing
 * content (the `ContentfulBlockHtml` escape-hatch field). Trust boundary is
 * "compromised Contentful editor account" — wider than inline/rich-text but
 * still blocks scripts, event handlers, and non-http(s) URIs.
 *
 * Not suitable for the Ghost blog body. A survey of all 396 published Ably
 * blog posts (built locally against Ghost) shows the body legitimately
 * contains HubSpot CTA scripts (~70 posts), Twitter widget scripts, YouTube
 * and Vimeo iframes (~50), and inline SVG diagrams (~130). Sanitising those
 * with this allowlist would silently break the site. Ghost body is treated
 * as a trusted-CMS surface in the ADR — editor account control is the
 * security boundary.
 *
 * Marketing copy needs headings, lists, code blocks, blockquotes, and
 * external links, so the URI rule allows http(s) instead of being clamped
 * to relative paths.
 */
export const sanitizeMarketingHtml = (input: string | null | undefined) =>
  DOMPurify.sanitize(input ?? "", {
    ALLOWED_TAGS: [
      "a",
      "b",
      "blockquote",
      "br",
      "code",
      "em",
      "figcaption",
      "figure",
      "h2",
      "h3",
      "h4",
      "i",
      "img",
      "li",
      "ol",
      "p",
      "pre",
      "span",
      "strong",
      "table",
      "tbody",
      "td",
      "th",
      "thead",
      "tr",
      "ul",
    ],
    ALLOWED_ATTR: ["alt", "class", "href", "src", "title"],
    // Either a full http(s):// URL, or a same-origin relative path whose
    // second character is neither `/` nor `\`. The earlier `/^(https?:)?\//`
    // was anchored only at the start, so `//evil.com` (protocol-relative)
    // survived: the regex matched the leading `/` and left the rest
    // unanchored. Browsers resolve `//evil.com` to the page scheme, so this
    // was an external-redirect bypass exactly in the threat model the
    // marketing sanitiser is meant to harden against (compromised Contentful
    // editor sneaking off-site links past review).
    ALLOWED_URI_REGEXP: /^(https?:\/\/|\/[^/\\])/,
  });
